// react
import { memo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
// mui components
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { LoadingButton } from "@mui/lab";

// user defined components, services, constants
import Logo from "src/components/Logo";
import { authService } from "src/services";
import enums from "src/util/enums";
import VerificationAndReset from "./VerificationAndReset";
import { resetPasswordValidationSchmea } from "src/util/validationSchemas";

// hooks
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

//actions
import { alertSlice } from "src/store/alert/alert.slice";

function ResetPasswordForm() {
  const cardRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [resetState, setResetState] = useState({ in: false, user: "" });
  const { mutateAsync, isLoading } = useMutation(authService.resetPassword);

  const onSubmit = async (values, { setSubmitting }) => {
    await mutateAsync(values, {
      onSuccess: () => {
        setResetState({
          in: !0,
          user: values.username,
        });
      },
      onError: (error) => {
        const {
          actions: { setAlert },
        } = alertSlice;
        dispatch(
          setAlert({
            open: true,
            message: error?.response?.data?.message ?? error?.message,
            severity: enums.SEVERITY.ERROR,
          })
        );
      },
    });
    setSubmitting(!1);
  };

  const formik = useFormik({
    initialValues: {
      username: "",
    },
    validationSchema: resetPasswordValidationSchmea,
    onSubmit,
  });
  const { errors, touched, handleSubmit, getFieldProps } = formik;

  return (
    <Container maxWidth="sm" className="reset-password" id="reset-password">
      <Box className="reset-password__main">
        <Logo />
        <Box className="reset-password__form" ref={cardRef}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              boxShadow: (theme) => theme.customShadows.z8,
            }}
          >
            {!resetState.in && (
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Paper
                  onSubmit={handleSubmit}
                  component="form"
                  elevation={0}
                  sx={{ p: 0 }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 2,
                      fontSize: { xs: "1.5rem", sm: "2rem" },
                      textAlign: "center",
                    }}
                  >
                    Password Assistance
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    Enter the username associated with the Amik cms
                  </Typography>
                  <FormControl fullWidth variant="standard" sx={{ mb: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Enter Username
                    </Typography>
                    <TextField
                      size="small"
                      disabled={isLoading}
                      error={Boolean(touched.username && errors.username)}
                      helperText={touched.username && errors.username}
                      {...getFieldProps("username")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </FormControl>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate("/login")}
                      disabled={isLoading}
                      sx={{
                        height: 48,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                        borderColor: "primary.main",
                        color: "primary.main",
                        "&:hover": {
                          borderColor: "primary.dark",
                          backgroundColor: "primary.light",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      fullWidth
                      variant="contained"
                      loading={isLoading}
                      sx={{
                        height: 48,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                      }}
                    >
                      Continue
                    </LoadingButton>
                  </Box>
                </Paper>
              </CardContent>
            )}
            <VerificationAndReset
              in={resetState.in}
              container={cardRef.current}
              user={resetState.user}
            />
          </Card>
        </Box>
      </Box>
    </Container>
  );
}

export default memo(ResetPasswordForm);
